export interface WorkflowStepLike {
  readonly id: string;
  readonly type: string;
  readonly label: string;
  readonly position: { readonly x: number; readonly y: number };
  readonly config: object;
}

export interface WorkflowLinkLike {
  readonly id: string;
  readonly source: string;
  readonly target: string;
  readonly sourceHandle?: string | undefined;
}

export interface WorkflowGraphShape {
  readonly nodes: ReadonlyArray<WorkflowStepLike>;
  readonly edges: ReadonlyArray<WorkflowLinkLike>;
  readonly layout?: string | undefined;
}

export interface WorkflowContent<G extends WorkflowGraphShape = WorkflowGraphShape> {
  readonly graph: G;
  readonly name: string;
  readonly description: string | null;
}

export type FacetKey =
  | { readonly kind: "step"; readonly id: string }
  | { readonly kind: "label"; readonly id: string }
  | { readonly kind: "position"; readonly id: string }
  | { readonly kind: "config"; readonly id: string; readonly key: string }
  | { readonly kind: "link"; readonly id: string }
  | { readonly kind: "name" }
  | { readonly kind: "description" }
  | { readonly kind: "layout" }
  | { readonly kind: "order"; readonly of: "nodes" | "edges" };

type StepFacetKey = Extract<FacetKey, { readonly kind: "step" | "label" | "position" | "config" }>;

export interface Facet {
  readonly key: FacetKey;
  readonly value: string;
  readonly raw: unknown;
}

export interface Facets<G extends WorkflowGraphShape = WorkflowGraphShape> {
  readonly byKey: ReadonlyMap<string, Facet>;
  readonly stepKeys: ReadonlyMap<string, ReadonlyArray<string>>;
  readonly nodes: ReadonlyMap<string, G["nodes"][number]>;
  readonly stepSignature: ReadonlyMap<string, string>;
  readonly stepContent: ReadonlyMap<string, string>;
}

export const isCosmetic = (key: FacetKey): boolean => key.kind === "position" || key.kind === "layout" || key.kind === "order";

export const isStepKey = (key: FacetKey): key is StepFacetKey =>
  key.kind === "step" || key.kind === "label" || key.kind === "position" || key.kind === "config";

export function keyString(key: FacetKey): string {
  switch (key.kind) {
    case "step":
    case "label":
    case "position":
    case "link":
      return `${key.kind}\u0000${key.id}`;
    case "config":
      return `config\u0000${key.id}\u0000${key.key}`;
    case "order":
      return `order\u0000${key.of}`;
    case "name":
    case "description":
    case "layout":
      return key.kind;
  }
}

function canonicalJson(value: unknown): string {
  if (value === undefined) return "null";
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  const record = value as Readonly<Record<string, unknown>>;
  const keys = Object.keys(record)
    .filter((key) => record[key] !== undefined)
    .sort();
  return `{${keys.map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`).join(",")}}`;
}

export function facetIn(key: string, ...sides: ReadonlyArray<Facets>): Facet {
  for (const side of sides) {
    const facet = side.byKey.get(key);
    if (facet) return facet;
  }
  throw new Error(`No version has the facet ${JSON.stringify(key)}.`);
}

export function facetsOf<G extends WorkflowGraphShape>(content: WorkflowContent<G>): Facets<G> {
  const byKey = new Map<string, Facet>();
  const stepKeys = new Map<string, string[]>();
  const nodes = new Map<string, G["nodes"][number]>();
  const stepSignature = new Map<string, string>();
  const stepContent = new Map<string, string>();
  const add = (key: FacetKey, raw: unknown): string => {
    const text = keyString(key);
    byKey.set(text, { key, value: canonicalJson(raw), raw });
    return text;
  };
  for (const node of content.graph.nodes) {
    const keys = [add({ kind: "step", id: node.id }, node.type), add({ kind: "label", id: node.id }, node.label)];
    const position = add({ kind: "position", id: node.id }, node.position);
    const config = node.config as Readonly<Record<string, unknown>>;
    for (const name of Object.keys(config).sort()) {
      if (config[name] !== undefined) keys.push(add({ kind: "config", id: node.id, key: name }, config[name]));
    }
    const text = keys.map((key) => `${key}=${(byKey.get(key) as Facet).value}`).join("\u0001");
    stepContent.set(node.id, text);
    stepSignature.set(node.id, `${text}\u0001${(byKey.get(position) as Facet).value}`);
    stepKeys.set(node.id, [...keys, position]);
    nodes.set(node.id, node);
  }
  for (const edge of content.graph.edges) add({ kind: "link", id: edge.id }, edge);
  add({ kind: "name" }, content.name);
  add({ kind: "description" }, content.description);
  if (content.graph.layout !== undefined) add({ kind: "layout" }, content.graph.layout);
  add(
    { kind: "order", of: "nodes" },
    content.graph.nodes.map((node) => node.id),
  );
  add(
    { kind: "order", of: "edges" },
    content.graph.edges.map((edge) => edge.id),
  );
  return { byKey, stepKeys, nodes, stepSignature, stepContent };
}

export function unionKeys(...sides: ReadonlyArray<Facets>): ReadonlyArray<string> {
  const keys = new Set<string>();
  for (const side of sides) for (const key of side.byKey.keys()) keys.add(key);
  return [...keys];
}

export const valueOf = (facet: Facet | undefined): string | undefined => facet?.value;

export function settleSteps(resolved: Map<string, Facet>, fallback: Facets): ReadonlySet<string> {
  const steps = new Map<string, { exists: boolean; label: boolean; position: boolean; keys: string[] }>();
  for (const [text, facet] of resolved) {
    if (!isStepKey(facet.key)) continue;
    const entry = steps.get(facet.key.id) ?? { exists: false, label: false, position: false, keys: [] };
    entry.keys.push(text);
    if (facet.key.kind === "step") entry.exists = true;
    if (facet.key.kind === "label") entry.label = true;
    if (facet.key.kind === "position") entry.position = true;
    steps.set(facet.key.id, entry);
  }
  const fellBack = new Set<string>();
  for (const [id, entry] of steps) {
    if (!entry.exists) {
      for (const text of entry.keys) resolved.delete(text);
    } else if (!entry.label || !entry.position) {
      takeStep(resolved, id, fallback);
      fellBack.add(id);
    }
  }
  return fellBack;
}

function takeStep(resolved: Map<string, Facet>, id: string, from: Facets): void {
  for (const [text, facet] of [...resolved]) if (isStepKey(facet.key) && facet.key.id === id) resolved.delete(text);
  for (const text of from.stepKeys.get(id) ?? []) resolved.set(text, from.byKey.get(text) as Facet);
}

export function contentOf<G extends WorkflowGraphShape>(
  resolved: ReadonlyMap<string, Facet>,
  template: WorkflowContent<G>,
  candidates: ReadonlyArray<Facets<G>>,
): WorkflowContent<G> {
  type Node = G["nodes"][number];
  type Edge = G["edges"][number];
  const stepFacets = new Map<string, Facet[]>();
  const links: Facet[] = [];
  for (const facet of resolved.values()) {
    if (isStepKey(facet.key)) {
      const list = stepFacets.get(facet.key.id) ?? [];
      list.push(facet);
      stepFacets.set(facet.key.id, list);
    } else if (facet.key.kind === "link") {
      links.push(facet);
    }
  }
  const nodes = new Map<string, Node>();
  for (const [id, facets] of stepFacets) {
    const signature = signatureOf(id, facets);
    const reused = candidates.find((side) => side.stepSignature.get(id) === signature)?.nodes.get(id);
    nodes.set(id, reused ?? buildNode<Node>(id, facets, candidates.find((side) => side.nodes.has(id))?.nodes.get(id)));
  }
  const edges = new Map<string, Edge>();
  for (const facet of links) {
    const edge = facet.raw as Edge;
    if (nodes.has(edge.source) && nodes.has(edge.target)) edges.set(edge.id, edge);
  }
  const order = (of: "nodes" | "edges", present: ReadonlySet<string>): ReadonlyArray<string> => {
    const skeleton = (resolved.get(keyString({ kind: "order", of }))?.raw ?? []) as ReadonlyArray<string>;
    const ordered = skeleton.filter((id) => present.has(id));
    const placed = new Set(ordered);
    const missing: Array<{ readonly id: string; readonly index: number }> = [];
    for (const id of present) {
      if (placed.has(id)) continue;
      let index = Number.MAX_SAFE_INTEGER;
      for (const side of candidates) {
        const list = (side.byKey.get(keyString({ kind: "order", of }))?.raw ?? []) as ReadonlyArray<string>;
        const at = list.indexOf(id);
        if (at >= 0) {
          index = at;
          break;
        }
      }
      missing.push({ id, index });
    }
    missing.sort((left, right) => left.index - right.index || (left.id < right.id ? -1 : left.id > right.id ? 1 : 0));
    for (const { id, index } of missing) ordered.splice(Math.min(index, ordered.length), 0, id);
    return ordered;
  };
  const nodeOrder = order("nodes", new Set(nodes.keys()));
  const edgeOrder = order("edges", new Set(edges.keys()));
  const { layout: _layout, ...graphRest } = template.graph;
  const layout = resolved.get(keyString({ kind: "layout" }))?.raw;
  const graph = {
    ...graphRest,
    nodes: nodeOrder.map((id) => nodes.get(id) as Node),
    edges: edgeOrder.map((id) => edges.get(id) as Edge),
    ...(layout === undefined ? {} : { layout }),
  } as unknown as G;
  return {
    ...template,
    graph,
    name: resolved.get(keyString({ kind: "name" }))?.raw as string,
    description: resolved.get(keyString({ kind: "description" }))?.raw as string | null,
  };
}

function signatureOf(id: string, facets: ReadonlyArray<Facet>): string {
  const byKey = new Map(facets.map((facet) => [keyString(facet.key), facet.value]));
  const head = [keyString({ kind: "step", id }), keyString({ kind: "label", id })];
  const config = [...byKey.keys()].filter((key) => key.startsWith("config\u0000")).sort();
  const sorted = [...head, ...config].filter((key) => byKey.has(key));
  const position = keyString({ kind: "position", id });
  return `${sorted.map((key) => `${key}=${byKey.get(key)}`).join("\u0001")}\u0001${byKey.get(position) ?? ""}`;
}

function buildNode<Node extends WorkflowStepLike>(id: string, facets: ReadonlyArray<Facet>, template: Node | undefined): Node {
  const fields: Record<string, unknown> = { ...template, id };
  const config: Record<string, unknown> = {};
  for (const facet of facets) {
    if (facet.key.kind === "step") fields.type = facet.raw;
    else if (facet.key.kind === "label") fields.label = facet.raw;
    else if (facet.key.kind === "position") fields.position = facet.raw;
    else if (facet.key.kind === "config") config[facet.key.key] = facet.raw;
  }
  const templateConfig = template ? Object.keys(template.config) : [];
  const configOrder = [...templateConfig.filter((key) => key in config), ...Object.keys(config).filter((key) => !templateConfig.includes(key)).sort()];
  fields.config = Object.fromEntries(configOrder.map((key) => [key, config[key]]));
  const keyOrder = template ? Object.keys(template) : [];
  for (const key of ["id", "type", "label", "position", "config"]) if (!keyOrder.includes(key)) keyOrder.push(key);
  return Object.fromEntries(keyOrder.map((key) => [key, fields[key]])) as unknown as Node;
}
